import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassFullIcon from "@mui/icons-material/HourglassFull";
import CADFlag from "assets/images/canada-flag.png";
import USAFlag from "assets/images/usa-flag.png";
import StripeLogo from "assets/images/stripe_logo.jpeg";
import {
  AdminOrderCard as StyledAdminOrderCard,
  CardBadge,
  PaymentIcon,
  OrderNumber,
  MetaList,
  MetaRow,
  MetaLabel,
  MetaValue,
  Chip,
  StatusBadge,
  CardFooter,
  FooterLeft,
  FooterRight,
  TotalPrice,
  FooterMeta,
} from "styles/pages/admin-dtf-orders";
import {
  formatOrderDate,
  formatCad,
  getOrderTotal,
  getOrderItems,
  getTotalItemCount,
  getOrderNumber,
  getOrderStatus,
  getPaymentStatus,
  formatShippingMethod,
} from "pages/dtf-transfers/orders/helpers";

const getCustomerEmail = (order) =>
  order?.user ??
  order?.checkout?.contact?.email ??
  order?.checkout?.shippingAddress?.email ??
  null;

const getOrderStatusTone = (status) => {
  const normalized = (status || "").toLowerCase().replace(/\s/g, "");
  if (["shipped", "delivered", "completed", "fulfilled"].includes(normalized)) {
    return "green";
  }
  if (["inreview", "review"].includes(normalized)) {
    return "review";
  }
  if (["cancelled", "canceled", "failed", "rejected"].includes(normalized)) {
    return "red";
  }
  return "orange";
};

const isPaymentSucceeded = (status) => {
  const normalized = (status || "").toLowerCase();
  return ["succeeded", "paid", "approve", "approved"].includes(normalized);
};

const getCountryCode = (order) => {
  const cardCountry =
    order?.charge?.payment_method_details?.card?.country ??
    order?.charge?.source?.country;

  if (cardCountry) return cardCountry;

  const shippingCountry = order?.checkout?.shippingAddress?.country;
  if (!shippingCountry) return null;

  if (
    shippingCountry.toLowerCase().includes("canada") ||
    shippingCountry === "CA"
  ) {
    return "CA";
  }
  if (
    shippingCountry.toLowerCase().includes("united states") ||
    shippingCountry === "US"
  ) {
    return "US";
  }

  return shippingCountry.slice(0, 2).toUpperCase();
};

const getCountryFlag = (countryCode) => {
  if (countryCode === "US") return USAFlag;
  return CADFlag;
};

const formatStatusLabel = (status) => {
  if (!status) return "—";
  return status
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ");
};

const AdminOrderCard = ({ order, onClick }) => {
  const items = getOrderItems(order);
  const itemCount = getTotalItemCount(items);
  const checkout = order?.checkout ?? {};
  const orderStatus = getOrderStatus(order);
  const paymentStatus = getPaymentStatus(order);
  const orderNumber = getOrderNumber(order);
  const customerEmail = getCustomerEmail(order);
  const countryCode = getCountryCode(order);
  const paymentSucceeded = isPaymentSucceeded(paymentStatus);

  return (
    <StyledAdminOrderCard
      onClick={() => onClick(order)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(order);
        }
      }}
    >
      <CardBadge>DTF</CardBadge>

      <PaymentIcon $succeeded={paymentSucceeded} aria-hidden>
        {paymentSucceeded ? (
          <CheckCircleIcon fontSize="inherit" />
        ) : (
          <HourglassFullIcon fontSize="inherit" />
        )}
      </PaymentIcon>

      <OrderNumber>#{orderNumber ?? "—"}</OrderNumber>

      <MetaList>
        {customerEmail ? (
          <MetaRow>
            <MetaLabel>Customer</MetaLabel>
            <MetaValue $accent $truncate title={customerEmail}>
              {customerEmail}
            </MetaValue>
          </MetaRow>
        ) : null}

        <MetaRow>
          <MetaLabel>Order Date</MetaLabel>
          <MetaValue>{formatOrderDate(order)}</MetaValue>
        </MetaRow>

        {checkout.shippingMethod ? (
          <MetaRow>
            <MetaLabel>Shipping</MetaLabel>
            <Chip>{formatShippingMethod(checkout.shippingMethod)}</Chip>
          </MetaRow>
        ) : null}

        {checkout.rushSameDay ? (
          <MetaRow>
            <MetaLabel>Rush</MetaLabel>
            <Chip>Same Day</Chip>
          </MetaRow>
        ) : null}

        <MetaRow>
          <MetaLabel>Status</MetaLabel>
          <StatusBadge $tone={getOrderStatusTone(orderStatus)}>
            {formatStatusLabel(orderStatus)}
          </StatusBadge>
        </MetaRow>

        {paymentStatus !== "—" ? (
          <MetaRow>
            <MetaLabel>Payment</MetaLabel>
            <Chip style={{ textTransform: "capitalize" }}>{paymentStatus}</Chip>
          </MetaRow>
        ) : null}
      </MetaList>

      <CardFooter>
        <FooterLeft>
          {order?.charge ? (
            <img src={StripeLogo} alt="Stripe" />
          ) : null}
        </FooterLeft>

        <FooterRight>
          <TotalPrice>
            {formatCad(
              getOrderTotal(order),
              order?.currency || order?.checkout?.currency || "CAD",
            )}
          </TotalPrice>
          <FooterMeta>
            {countryCode ? (
              <>
                <img src={getCountryFlag(countryCode)} alt={countryCode} />
                {countryCode}
              </>
            ) : null}
            {itemCount > 0 ? (
              <span>
                {countryCode ? " · " : ""}
                {itemCount} item{itemCount === 1 ? "" : "s"}
              </span>
            ) : null}
          </FooterMeta>
        </FooterRight>
      </CardFooter>
    </StyledAdminOrderCard>
  );
};

export default AdminOrderCard;
